import React, { useState, useMemo, useEffect } from 'react';
import { StepIndicator } from '../../primitives/StepIndicator';
import { THEMES } from '../../../themes/themes';
import { BeginStep } from './BeginStep';
import { MeetStep } from './MeetStep';
import { SealStep } from './SealStep';
import { ThemeVariantModal } from './ThemeVariantModal';
import { validateBeginStep } from './validateBeginStep';
import {
  ALL_HOMEBREW_KEYS,
  FEATURES,
  DEFAULT_TEST_PRESET_CATEGORIES,
  LINK_OPTIONS,
  THEME_VARIANTS,
  TOTAL_STEPS,
} from './constants';
import { MOCK_EXISTING_CAMPAIGNS, MOCK_WORLDS } from './mockData';

// Tiny hex → rgba helper for the temporary backdrop tuning slider. Only
// handles 6-char hex (e.g. '#9b59b6'). Returns a default purple if parsing
// fails so the modal still renders something reasonable.
const hexToRgba = (hex, alpha) => {
  if (typeof hex !== 'string') return `rgba(155, 89, 182, ${alpha})`;
  const h = hex.replace('#', '');
  if (h.length !== 6) return `rgba(155, 89, 182, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(155, 89, 182, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const normalizeWorldRows = (rows = []) => (Array.isArray(rows) ? rows : [])
  .map((row) => ({
    id: row.id || row.worldId,
    name: row.name || 'Untitled world',
    tagline: row.tagline || row.description || '',
    loreCount: row.loreCount || 0,
    atlasPlaceCount: row.atlasPlaceCount || 0,
  }))
  .filter((row) => row.id);

const normalizeExistingCampaignRows = (rows = []) => (Array.isArray(rows) ? rows : [])
  .map((row) => ({
    id: row.id || row.campaignId,
    worldId: row.worldEntityId || row.worldId || null,
    name: row.name || 'Untitled campaign',
    tagline: row.tagline || row.blurb || '',
    dm: row.dm || '',
    players: row.players || 0,
    homebrew: row.homebrew ?? null,
    enabledFeatures: row.enabledFeatures || row.features || {},
  }))
  .filter((row) => row.id);

// Build the initial form state. Mirrors the prototype's `form` shape
// 1:1 — every key the F.6 adapter will read maps directly to a field
// here. Defaults align with the prototype so users land on "scratch
// origin / isolated world / no template / all features enabled".
const buildInitialForm = (themeProp, existingCampaigns) => ({
  name: '',
  tagline: '',
  baseTheme: themeProp?.id || 'fantasy-dark',
  baseVariant: (THEME_VARIANTS[themeProp?.id || 'fantasy-dark']?.[0]?.id) || '',
  origin: 'scratch',
  templateId: '',
  existingId: '',
  worldMode: 'isolated',
  worldId: '',
  newWorldName: '',
  linkModes: Object.fromEntries(LINK_OPTIONS.map((l) => [l.key, 'off'])),
  daySchedule: {},
  scheduleTBD: false,
  invitedCollaborators: [],
  invitedEmails: [],
  dummyCount: 4,
  testPresetCategories: { ...DEFAULT_TEST_PRESET_CATEGORIES },
  testCharacterPresetCategories: { ...DEFAULT_TEST_PRESET_CATEGORIES },
  features: Object.fromEntries(FEATURES.map((f) => [f.key, true])),
  rulesMode: existingCampaigns.length > 0 ? 'default' : '5e',
  copyFromId: existingCampaigns[1]?.id || existingCampaigns[0]?.id || '',
  homebrew: Object.fromEntries(ALL_HOMEBREW_KEYS.map((k) => [k, false])),
});

const resetUnavailableExistingSource = (prev) => ({
  ...prev,
  origin: 'scratch',
  existingId: '',
  features: Object.fromEntries(FEATURES.map((f) => [f.key, true])),
  homebrew: Object.fromEntries(ALL_HOMEBREW_KEYS.map((k) => [k, false])),
});

// CampaignForgeV2 — 3-step fullscreen wizard for creating a new campaign.
// Ported from prototypes/campaign-creation-prototype.jsx
// (CampaignCreationView, lines 2791-3045). Live mounts pass real worlds and
// DM-owned campaign rows; fixture defaults keep prototype/story usage working.
//
// Z-index: 220 (matches the legacy CampaignForge); ThemeVariantModal uses 230.
// Theme preview is LIVE — changing baseTheme/baseVariant inside the modal
// updates `effectiveTheme` immediately so the wizard re-renders with the
// selected palette while the user is still picking.
export const CampaignForgeV2 = ({
  theme: themeProp,
  worlds = MOCK_WORLDS,
  worldsLoading = false,
  existingCampaigns = MOCK_EXISTING_CAMPAIGNS,
  sourceImportEnabled = true,
  onCancel,
  onCreate,
  onPreviewSandbox,
}) => {
  const worldOptions = useMemo(() => normalizeWorldRows(worlds), [worlds]);
  const existingCampaignOptions = useMemo(
    () => normalizeExistingCampaignRows(existingCampaigns),
    [existingCampaigns],
  );
  const [step, setStep] = useState(1);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [form, setForm] = useState(() => buildInitialForm(themeProp, existingCampaignOptions));
  // Submit state — busy flag flips the Next button to "Forging…" while
  // onCreate is in flight; error surfaces the message inline above the
  // footer if the promise rejects.
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (form.origin !== 'existing') return;
    if (!sourceImportEnabled) {
      setForm((prev) => (
        prev.origin === 'existing'
          ? resetUnavailableExistingSource(prev)
          : prev
      ));
      return;
    }
    if (form.existingId && !existingCampaignOptions.some((camp) => camp.id === form.existingId)) {
      setForm((prev) => (
        prev.origin === 'existing' && prev.existingId === form.existingId
          ? resetUnavailableExistingSource(prev)
          : prev
      ));
    }
  }, [existingCampaignOptions, form.existingId, form.origin, sourceImportEnabled]);

  // Live-preview theme: resolve from form.baseTheme + form.baseVariant.
  // Falls back to the prop theme if the form's baseTheme isn't known.
  // The variant overrides the theme's accent/accentLight so the picker
  // swatches and chrome reflect the selected variant immediately.
  //
  // Tightened (2026-05-19): when the wizard's baseTheme matches the
  // dashboard's themeProp.id, we keep using themeProp directly so any
  // variation / font overrides the dashboard applied stay intact. Only
  // when the user picks a DIFFERENT theme in the wizard's picker do we
  // jump to the raw THEMES[id] palette for live preview.
  const effectiveTheme = useMemo(() => {
    const base = (themeProp && form.baseTheme === themeProp.id)
      ? themeProp
      : (THEMES[form.baseTheme] || themeProp);
    const variants = THEME_VARIANTS[form.baseTheme] || [];
    const variant = variants.find((v) => v.id === form.baseVariant);
    if (!variant) return base;
    return {
      ...base,
      colors: {
        ...base.colors,
        accent: variant.accent,
        accentLight: variant.accentLight,
      },
    };
  }, [form.baseTheme, form.baseVariant, themeProp]);

  const c = effectiveTheme.colors;
  const t = effectiveTheme.typography || {};

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const canAdvance = (() => {
    if (step === 1) return validateBeginStep(form);
    return true; // F.4/F.5 will gate steps 2 + 3 when those lands.
  })();

  const handleNext = async () => {
    if (!canAdvance || busy) return;
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      return;
    }
    if (!onCreate) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(form);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const handlePreviewSandbox = async () => {
    if (!canAdvance || busy || !onPreviewSandbox) return;
    setBusy(true);
    setError(null);
    try {
      await onPreviewSandbox(form);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleJump = (target) => {
    if (target < step) setStep(target);
  };

  // Shared button styles — kept inline (matching dashboard chrome
  // convention of inlined style helpers per modal).
  const ghostBtnStyle = {
    background: 'transparent',
    border: `1px solid ${c.borderLight}`,
    borderRadius: 6,
    padding: '8px 18px',
    color: c.textSecondary,
    fontFamily: t.fontFamily,
    fontSize: '0.88rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 120ms ease',
  };
  const primaryBtnStyle = {
    background: c.accent,
    border: `1px solid ${c.accent}`,
    borderRadius: 6,
    padding: '8px 22px',
    color: c.textOnAccent,
    fontFamily: t.fontFamily,
    fontSize: '0.92rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 120ms ease',
  };

  let stepContent;
  if (step === 1) {
    stepContent = (
      <BeginStep
        theme={effectiveTheme}
        form={form}
        update={update}
        worlds={worldOptions}
        worldsLoading={worldsLoading}
        existingCampaigns={existingCampaignOptions}
        sourceImportEnabled={sourceImportEnabled}
        onOpenThemePicker={() => setThemePickerOpen(true)}
      />
    );
  } else if (step === 2) {
    stepContent = (
      <MeetStep
        theme={effectiveTheme}
        form={form}
        update={update}
        worlds={worldOptions}
        existingCampaigns={existingCampaignOptions}
      />
    );
  } else {
    stepContent = (
      <SealStep
        theme={effectiveTheme}
        form={form}
        update={update}
        worlds={worldOptions}
        existingCampaigns={existingCampaignOptions}
      />
    );
  }

  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-label="Create a new campaign"
      style={{
        position: 'fixed', inset: 0,
        // Blurred themed backdrop — pulls the current variant's accent
        // (e.g. amethyst for Dark Arcana) at 0.06 alpha so the dashboard
        // behind softens into a faint colored haze that matches whichever
        // theme the user has picked. Tuned via the dev slider; lower felt
        // too transparent, higher washed out the modal.
        background: hexToRgba(c.accent, 0.06),
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 220,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 760,
          // Cap at a fixed pixel value (not a vh fraction) so the modal
          // reads as the same compact card the prototype shows, regardless
          // of how tall the user's viewport is. On a 1080p browser 86vh
          // would balloon to ~920px and Step 2 reads as a full-page
          // takeover; this caps it so Step 2 scrolls inside instead.
          maxHeight: 'min(86vh, 750px)',
          // Card surface uses `bgPrimary` (near-black `#000001` for
          // fantasy-dark) to match the prototype. Using `bgCardSolid`
          // (#1c1028 plum) made the card read as the same dark-purple
          // wash as the dashboard's page background; bgPrimary sits as a
          // distinct near-black surface against the overlay.
          background: c.bgPrimary,
          border: `1px solid ${c.borderLight}`,
          borderRadius: 12,
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.65)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          color: c.textPrimary,
          fontFamily: t.fontFamily,
        }}
      >
        {/* Header strip */}
        <div style={{
          padding: '12px 16px 12px 22px',
          borderBottom: `1px solid ${c.borderLight}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flex: '0 0 auto',
        }}>
          <div style={{
            fontFamily: t.fontFamily,
            fontSize: '0.78rem',
            letterSpacing: '0.22em',
            color: c.textMuted,
            textTransform: 'uppercase',
            fontWeight: 600,
          }}>New Campaign · Step {step} of {TOTAL_STEPS}</div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: c.textMuted,
              fontSize: '1.3rem',
              cursor: 'pointer',
              padding: '0 6px',
              lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Themed scrollbar — keeps the inner scroll track from rendering as
            the OS default light-grey bar against the dark wizard chrome.
            Scoped to the wizard's body via the data attribute so it doesn't
            leak into the rest of the app. */}
        <style>{`
          [data-forge-v2-body]::-webkit-scrollbar { width: 10px; }
          [data-forge-v2-body]::-webkit-scrollbar-track { background: ${c.bgInput || 'rgba(0,0,0,0.3)'}; }
          [data-forge-v2-body]::-webkit-scrollbar-thumb { background: ${c.borderPrimary}; border-radius: 5px; }
          [data-forge-v2-body]::-webkit-scrollbar-thumb:hover { background: ${c.accent}; }
        `}</style>

        {/* Scrollable body */}
        <div
          data-forge-v2-body
          style={{
            flex: 1,
            overflow: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.borderPrimary} ${c.bgInput || 'rgba(0,0,0,0.3)'}`,
          }}
        >
          {stepContent}
        </div>

        {/* Inline error pill — surfaces server failures from the final
            onCreate submit. Sits above the footer so it stays visible even
            when the body scrolls. */}
        {error && (
          <div
            role="alert"
            style={{
              margin: '0 22px 12px',
              padding: '8px 12px',
              background: 'rgba(212,106,90,0.15)',
              border: `1px solid ${c.danger || '#d46a5a'}`,
              color: c.danger || '#d46a5a',
              borderRadius: 4,
              fontSize: '0.86rem',
              fontFamily: t.fontFamily,
              flex: '0 0 auto',
            }}
          >{error}</div>
        )}

        {/* Footer strip: step indicator + Back / Next */}
        <div style={{
          padding: '10px 22px 14px',
          borderTop: `1px solid ${c.borderLight}`,
          background: c.bgCardSolid,
          flex: '0 0 auto',
        }}>
          <StepIndicator step={step} totalSteps={TOTAL_STEPS} theme={effectiveTheme} onJump={handleJump} />
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 8,
          }}>
            {step > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={busy}
                style={{
                  ...ghostBtnStyle,
                  opacity: busy ? 0.5 : 1,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >← Back</button>
            ) : (
              <span style={{ visibility: 'hidden' }}>
                <button type="button" style={ghostBtnStyle}>← Back</button>
              </span>
            )}
            {step === TOTAL_STEPS && onPreviewSandbox && (
              <button
                type="button"
                onClick={handlePreviewSandbox}
                disabled={!canAdvance || busy}
                style={{
                  ...ghostBtnStyle,
                  opacity: (canAdvance && !busy) ? 1 : 0.45,
                  cursor: busy ? 'wait' : (canAdvance ? 'pointer' : 'not-allowed'),
                }}
              >
                Replace preview sandbox
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance || busy}
              style={{
                ...primaryBtnStyle,
                opacity: (canAdvance && !busy) ? 1 : 0.45,
                cursor: busy ? 'wait' : (canAdvance ? 'pointer' : 'not-allowed'),
              }}
            >
              {busy ? 'Forging…' : (step < TOTAL_STEPS ? 'Next →' : 'Begin the campaign')}
            </button>
          </div>
        </div>
      </div>

      <ThemeVariantModal
        open={themePickerOpen}
        onClose={() => setThemePickerOpen(false)}
        currentTheme={effectiveTheme}
        form={form}
        update={update}
      />
    </div>
  );
};
